import React from "react";

import _ from "lodash";
import { Alert } from "react-bs-notifier";

import { fieldNames } from "../utilities/constants";

const {
  RACE,
  LEGISLATIVE_CONFERENCE,
  GENDER,
  COMMITTEE_ASSIGNMENTS_AND_CAUCUSES,
  REGION,
  SEXUAL_ORIENTATION,
} = fieldNames;

const unknown = "Unknown";

const utils = {
  showNotice: function Notice() {
    return (
      <Alert type="warning">
        Need a list of legislators, their staff, and their contact info? Log in
        to
        <a
          href="https://mytools.advocacy-institute.org/"
          className="alert-link"
          target="_blank"
          rel="noreferrer"
        >
          {" "}
          My Tools{" "}
        </a>
        and go to Find Legislators. If you would like Advocacy Institute to
        generate a list for you, click
        <a
          href="https://airtable.com/shr49NADdD9nvYPwD"
          className="alert-link"
          target="_blank"
          rel="noreferrer"
        >
          {" "}
          here{" "}
        </a>
        to submit a request.
        <br />
      </Alert>
    );
  },

  sortByRaceAndName: function (people) {
    let sortedPeople = [];
    const byRace = _.sortBy(people, function (person) {
      return person.fields[RACE];
    });
    const group = _.groupBy(byRace, function (person) {
      return person.fields[RACE];
    });

    Object.keys(group).forEach((key) => {
      sortedPeople.push(_.sortBy(group[key], `fields["Last Name"]`));
    });
    return _.flatten(sortedPeople);
  },

  sortByRacePartyAndName: function (people) {
    let sortedParties = [];

    const byRace = _.sortBy(people, function (person) {
      return person.fields[RACE];
    });
    const group = _.groupBy(byRace, function (person) {
      return person.fields[RACE];
    });

    //sort party group by race and last name
    Object.keys(group).forEach((key) => {
      sortedParties.push(
        _.sortBy(
          group[key],
          `fields["Legislative Conference"]`,
          `fields["Last Name"]`
        )
      );
    });

    return _.flatten(sortedParties);
  },

  setCommitteeOptions: function (people) {
    const committees = people.reduce(function (committees, person) {
      const assignments = person.fields[COMMITTEE_ASSIGNMENTS_AND_CAUCUSES];
      assignments &&
        assignments.forEach((committee) => {
          if (!committees.includes(committee)) {
            committees.push(committee);
          }
        });
      return committees;
    }, []);

    const committeeOptions = committees
      .sort()
      .reduce(function (committeeObjects, c) {
        let committee = Object.assign({ value: c }, { label: c });
        committeeObjects.push(committee);
        return committeeObjects;
      }, []);

    return committeeOptions;
  },

  generateLegendCategories: function () {
    const races = ["Arab/Asian", "Black", "Latine", "White", unknown];
    const parties = [
      "Democratic",
      "Republican",
      "Conservative",
      "Independent",
      "Undecided",
      unknown,
    ];

    const normalizeRace = (race) => race.toLowerCase().replace(/\W+/g, "-");

    const legendCategories = [];

    parties.forEach((party) => {
      races.forEach((race) => {
        let id = "";
        if (party === unknown) {
          id = "unknown";
          race = unknown;
          party = unknown;
        } else if (party === "Undecided") {
          id = "undecided";
        } else {
          id = `${normalizeRace(race)}-${party.toLowerCase()}`; // Normal race-party combination
        }

        // Only add new categories if they don't already exist to avoid duplicates
        if (
          !legendCategories.some((category) => {
            if (category.race === race && category.party === party) {
              return true;
            }
            return false;
          })
        ) {
          legendCategories.push({ race, party, id });
        }
      });
    });
    return legendCategories;
  },

  renderLegend: function ({ people, title }) {
    const legendCategories = this.generateLegendCategories();
    return legendCategories.map((category) => {
      let legendCount = getLegendCount({
        people: people,
        category: category,
      });

      let unknownRace = category.race === unknown;
      let unknownParty = category.party === unknown;

      if (legendCount > 0) {
        return (
          <div key={category.id} className="legend-lbl">
            <div className="round-button">
              <div className={getClasses(category.id)} />
            </div>
            <h6 className="lblname">
              {category.party === "Undecided"
                ? `Pending Election Outcome ${legendCount}`
                : unknownParty
                ? `${unknown} Conference ${legendCount}`
                : unknownRace
                ? `${unknown} Race ${category.party} ${title} ${legendCount}`
                : `${category.race} ${category.party} ${title} ${legendCount}`}
            </h6>
          </div>
        );
      }
      return null;
    });
  },
  updateCounts: function ({
    peopleByGroup,
    genderFilter,
    raceFilter,
    regionFilter = null,
    committeeFilter,
  }) {
    const byGender = genderCount(peopleByGroup, genderFilter);
    const byRace = raceCount(peopleByGroup, raceFilter);
    const byRegion = regionCount(peopleByGroup, regionFilter);
    const byCommittee = committeeCount(peopleByGroup, committeeFilter);
    return _.intersection(byRace, byGender, byRegion, byCommittee).length;
  },

  trimRegion: function (regionStr) {
    let regions = regionStr.split(",");
    return regions.map((region) => {
      return region.trim().slice(0, -17);
    }); // trim off whitespace and "State EDC Region"
  },

  renderIdentities(repData, boldClassName = "", heading = true) {
    let race = repData[RACE];
    const gender = repData[GENDER];
    const sexualOrientation = repData[SEXUAL_ORIENTATION];
    const identities = [race, gender, sexualOrientation];

    //if race, gender and sexual orientatiton are null, they will be set to "unknown" here
    const knownIdentities = identities.filter((identity) => {
      return identity && identity !== "Unknown";
    });
    const strIdentities = knownIdentities.join(", ");
    return (
      <div className="identity-info">
        {race || gender || sexualOrientation ? (
          heading ? (
            <h5> Seems to identify as:</h5>
          ) : (
            "Seems to identity as: "
          )
        ) : null}
        <span className={boldClassName}>{strIdentities}</span>
      </div>
    );
  },
};

function getClasses(value) {
  return `round-button-circle ${value}`;
}

function getLegendCount({ people, category }) {
  /*
   * category is an object from the array of legendCategories in constants.js
   * eg. { race: "White", party: "Republican", id: "white-republican" }
   */
  const party = category.party;
  const race = category.race;

  const filteredPeople = people.filter((person) => {
    const legislativeConference = person?.fields?.[LEGISLATIVE_CONFERENCE];
    let legislativeConferenceStr = "";

    if (Array.isArray(legislativeConference) || !legislativeConference) {
      legislativeConferenceStr = unknown;
    } else {
      legislativeConferenceStr = legislativeConference;
    }

    const personRaces = person.fields[RACE] || [unknown];

    if (party === unknown && race === unknown) {
      // We only care about unknown party, because people with known party + unknown race are handled separately
      return legislativeConferenceStr.includes(party);
    } else {
      return (
        legislativeConferenceStr.includes(party) && personRaces.includes(race)
      );
    }
  });

  return filteredPeople.length;
}

function genderCount(peopleByGroup, genderFilter) {
  if (genderFilter && genderFilter.length > 0) {
    return peopleByGroup.filter((rep) => {
      const gender = rep.fields?.[GENDER]?.[0];
      return gender && genderFilter.includes(gender);
    });
  } else {
    return peopleByGroup;
  }
}

function raceCount(peopleByGroup, raceFilter) {
  if (raceFilter.length > 0) {
    let filteredPeople = [];
    peopleByGroup.forEach((person) => {
      let race = person.fields[RACE] || [unknown];
      race.forEach((race) => {
        if (raceFilter.includes(race)) {
          filteredPeople.push(person);
        }
      });
    });
    return filteredPeople;
  } else {
    return peopleByGroup;
  }
}

function regionCount(peopleByGroup, regionFilter) {
  if (regionFilter) {
    return peopleByGroup.filter((rep) => {
      const regions = utils.trimRegion(rep.fields[REGION]);
      let includesRegion = false;
      regions.forEach((region) => {
        if (regionFilter.includes(region)) {
          includesRegion = true;
        }
      });
      return includesRegion;
    });
  } else {
    return peopleByGroup;
  }
}

function committeeCount(peopleByGroup, committeeFilter) {
  if (committeeFilter.length > 0) {
    return peopleByGroup.filter((person) => {
      const repCommitteesList =
        person.fields[COMMITTEE_ASSIGNMENTS_AND_CAUCUSES];
      return (
        committeeFilter.filter((committee) => {
          return (
            repCommitteesList &&
            repCommitteesList.find((repCommittee) => repCommittee === committee)
          );
        }).length > 0
      );
    });
  } else {
    return peopleByGroup;
  }
}

export default utils;
